import { ampli } from '@/ampli';
import { useState } from 'react';

import { SecurityModal } from '@/components/Modal/SecurityModuleModal';

import { fontStyles } from '@/styles/typography';

import { LoadingSpinnerPrimary } from '@/ui/Icons/Animated/LoadingSpinner';
import { InfoCircleSolidSmallIcon } from '@/ui/Icons/InfoCircleSolidSmallIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
  MatchaTooltipBottomSheet,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';

import {
  SECURITY_LEVEL_TO_BADGE_VARIANT,
  getSecurityLabel,
  useSecurityAssessment,
} from '../../../hooks/useSecurityAssessment';
import { ScoreSectionLoader } from '../ScoreSectionLoader';
import { BadgeIcon } from './RiskIcons/BadgeIcon';
import {
  RiskLevelIcon,
  PositiveIcon,
  AttentionIcon,
  UnknownIcon,
} from './RiskIcons/RiskLevelIcons';
import {
  SECURITY_LEVEL_GRADIENT,
  securityLevelContainerBg,
  securityLevelTextColor,
} from './styles';

interface TokenProfileSecurityProps {
  activeToken: TokenResult | undefined;
  isLoading: boolean;
}

export const TokenProfileSecurity = ({ activeToken, isLoading }: TokenProfileSecurityProps) => {
  if (isLoading || !activeToken) {
    return (
      <div className="flex flex-col scroll-mt-[62px]">
        <ScoreSectionLoader />
      </div>
    );
  }

  return <TokenProfileSecurityContent activeToken={activeToken} />;
};

interface ContentProps {
  activeToken: TokenResult;
}

const TokenProfileSecurityContent = ({ activeToken }: ContentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { level, risks, highRisks, alerts, includesDedaubScan, isDedaubScanLoading } =
    useSecurityAssessment({ activeToken });

  const gradient = SECURITY_LEVEL_GRADIENT[level];
  const badgeVariant = SECURITY_LEVEL_TO_BADGE_VARIANT[level];
  const securityLabel = getSecurityLabel(level, risks + highRisks);

  return (
    <div className="flex flex-col scroll-mt-[62px]" id="securityAudit">
      <h2 className={fontStyles({ variant: 'H5', class: ['flex', ' px-6', ' pt-6'] })}>
        Security Audit
      </h2>
      <div className="grid gap-6 grid-cols-1 tablet:grid-cols-2 h-full auto-rows-fr p-6">
        <div
          className={classNames(
            'border border-solid border-token-gray-200',
            securityLevelContainerBg({
              level,
              class: [
                'flex',
                'flex-col',
                'items-center',
                'justify-center',
                'rounded-[16px]',
                'px-6',
                'relative',
                'overflow-hidden',
              ],
            }),
          )}
        >
          <div
            className="rotate-[-16.841deg] absolute top-[-25%] left-0 w-full h-full overflow-hidden pointer-events-none x-0"
            style={gradient}
          />
          <BadgeIcon variant={badgeVariant} size={32} />
          <p
            className={fontStyles({
              variant: 'LABEL_XS',
              class: ['mt-3', 'flex', 'justify-center', 'mb-4', securityLevelTextColor({ level })],
            })}
          >
            {securityLabel}
          </p>
        </div>
        <div className="flex flex-col p-4 rounded-[16px] bg-token-alphalight-100  border border-solid border-token-gray-200">
          <div
            className={fontStyles({ variant: 'LABEL_XS', class: ['mb-4', 'text-token-gray-800'] })}
          >
            {includesDedaubScan ? 'Security checks' : 'Go+ Security'}
          </div>
          {alerts.previewAlerts.map((alert, index) => (
            <div key={index} className="flex justify-between items-center py-1">
              <MatchaTooltip delayDuration={0}>
                <MatchaTooltipTrigger>
                  <div
                    className={fontStyles({ variant: 'BODY_SM', class: ['flex', 'items-center'] })}
                  >
                    {alert.label}
                    <InfoCircleSolidSmallIcon
                      width={10}
                      height={10}
                      className="ml-1 text-token-gray-800"
                    />
                  </div>
                </MatchaTooltipTrigger>
                <MatchaTooltipContent>{alert.tooltipInfo}</MatchaTooltipContent>
                <MatchaTooltipPopover
                  align="end"
                  alignOffset={-125}
                  className="block max-w-[224px]"
                  side="top"
                />
                <MatchaTooltipBottomSheet header="Security Score" />
              </MatchaTooltip>
              <span
                className={fontStyles({ variant: 'BODY_SM', class: ['flex', 'center', 'gap-2'] })}
              >
                {alert.isDedaubCheck && isDedaubScanLoading ? 'Checking' : alert.valueLabel}
                {alert.isDedaubCheck && isDedaubScanLoading ? (
                  <LoadingSpinnerPrimary className="ml-0 shrink-0" />
                ) : alert.type === 'positive' ? (
                  <PositiveIcon />
                ) : alert.type === 'risk' ? (
                  <RiskLevelIcon />
                ) : alert.type === 'attention' ? (
                  <AttentionIcon />
                ) : alert.type !== 'number' ? (
                  <UnknownIcon />
                ) : null}
              </span>
            </div>
          ))}
        </div>
      </div>

      <button
        className={fontStyles({
          variant: 'LABEL_MD',
          class: [
            'cursor-pointer text-center text-token-gray w-full h-[44px] border-t-token-gray-200 text-token-gray-800 border-t-[1px] rounded-b-2xl hover:bg-token-alphalight-100',
          ],
        })}
        onClick={() => {
          setIsOpen(true);
          ampli.securityAuditLearnMoreClicked({
            token_address: activeToken.address,
            token_chain_id: activeToken.chainId,
            token_chain_name: activeToken.chainName,
            token_symbol: activeToken.symbol,
          });
        }}
      >
        Learn more
      </button>
      <SecurityModal activeToken={activeToken} isOpen={isOpen} onOpenChange={setIsOpen} />
    </div>
  );
};
